import { useMemo } from 'react';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { getCalculateBucketFunctionOptions } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/activeBoundaryFillEditComponent';
import { useGetBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';

const getBoundaryFillEntities = ({
  boundaryTerritoryGroup,
  bucketFunctionOptionName,
  columnName,
  getBoundaryGroupById,
  boundaryTerritoryType,
  t,
}: Readonly<{
  t: TranslationFnc;
  boundaryTerritoryGroup?: BoundaryTerritoryGroup;
  bucketFunctionOptionName: string | undefined;
  columnName: string;
  getBoundaryGroupById: ReturnType<typeof useGetBoundaryGroup>['getBoundaryGroupById'];
  boundaryTerritoryType: BoundaryTerritoryType;
}>) => {
  if (!boundaryTerritoryGroup) {
    return null;
  }

  switch (boundaryTerritoryType) {
    case BoundaryTerritoryType.Density: {
      return { mainEntity: t('Location Density') };
    }

    case BoundaryTerritoryType.Demographic: {
      if (boundaryTerritoryGroup.settings.demographicId) {
        const demographics = getBoundaryGroupById(boundaryTerritoryGroup.boundaryGroupId)?.demographics;
        if (demographics) {
          const selectedDemographic = demographics.find(d => d.id === boundaryTerritoryGroup.settings.demographicId);
          if (selectedDemographic) {
            return {
              mainEntity: selectedDemographic.category,
              subEntity: selectedDemographic.name,
            };
          }
        }
      }
      return null;
    }

    case BoundaryTerritoryType.Groups: {
      return { mainEntity: t('Group'), subEntity: columnName };
    }

    case BoundaryTerritoryType.Numeric: {
      if (bucketFunctionOptionName) {
        return { mainEntity: bucketFunctionOptionName, subEntity: columnName };
      }
      return null;
    }

    case BoundaryTerritoryType.Manual:
    default: {
      return null;
    }
  }
};

export const useGetBoundaryFillEntities = ({
  boundaryTerritoryGroup,
  boundaryTerritoryType,
  calculateBucketFunction,
  columnId,
}: {
  columnId?: string | null;
  calculateBucketFunction?: string | null;
} & Pick<Parameters<typeof getBoundaryFillEntities>[0], 'boundaryTerritoryType' | 'boundaryTerritoryGroup'>) => {
  const [t] = useTranslation();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const spreadSheetColumns = useSpreadsheetColumns();

  const columnName = spreadSheetColumns.find(column => column.id === columnId)?.name ??
    t('Unknown column');

  const bucketFunctionOptionName = getCalculateBucketFunctionOptions(t)
    .find(option => option.value === calculateBucketFunction)?.name;

  return useMemo(() => getBoundaryFillEntities({
    boundaryTerritoryGroup,
    boundaryTerritoryType,
    bucketFunctionOptionName,
    columnName,
    getBoundaryGroupById,
    t,
  }), [boundaryTerritoryGroup, bucketFunctionOptionName, columnName, getBoundaryGroupById, boundaryTerritoryType, t]);
};
